import { Injectable, inject, ApplicationRef, isDevMode } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { interval, concat, filter } from 'rxjs';
import { first } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { AuthStore } from '@app/core/auth/stores/auth-store';
import { Router } from '@angular/router';
import { devLog, devWarn, devError } from '@app/core/utils/dev-logger';

/**
 * PWA Update Service
 *
 * Handles app version updates with automatic logout to ensure clean cache refresh.
 *
 * Features:
 * - Periodic update checks (every 30 minutes)
 * - Immediate update check on app start (after stable)
 * - Forces logout before update to clear all caches
 * - User notification with countdown
 * - Graceful handling of update failures
 * - Only active in production (service worker enabled)
 *
 * Update Flow:
 * 1. New version detected
 * 2. Notify user with countdown (30 seconds)
 * 3. Force logout (clears all caches)
 * 4. Activate new version
 * 5. Reload page to /login
 *
 * Why Force Logout:
 * - Ensures all in-memory caches are cleared
 * - Prevents stale data from old version
 * - Cleans up IndexedDB caches
 * - Resets authentication state
 * - Provides clean slate for new version
 */
@Injectable({ providedIn: 'root' })
export class PwaUpdateService {
  private readonly updates = inject(SwUpdate);
  private readonly appRef = inject(ApplicationRef);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly authStore = inject(AuthStore);
  private readonly router = inject(Router);
  private readonly updateFlagKey = 'pwa-update-pending';

  // Countdown before update applies
  private readonly UPDATE_COUNTDOWN_SECONDS = 30;
  private countdownInterval?: ReturnType<typeof setInterval>;

  private isKarmaEnvironment(): boolean {
    return typeof (window as any)?.__karma__ !== 'undefined';
  }

  /**
   * Initialize update checking
   * Call this from app initialization
   */
  initialize(): void {
    if (this.isKarmaEnvironment()) {
      devLog('pwa-update', '⚠️ Karma detected - skipping PWA update initialization');
      return;
    }

    if (!this.updates.isEnabled) {
      devLog('pwa-update', '⚠️ Service Worker not enabled - updates disabled');
      return;
    }

    devLog('pwa-update', '🔄 PWA Update Service initialized');

    // Check for updates periodically
    this.scheduleUpdateChecks();

    // Listen for version updates
    this.listenForUpdates();

    // Handle unrecoverable state
    this.handleUnrecoverableState();
  }

  /**
   * Schedule periodic update checks
   * - Checks immediately when app becomes stable
   * - Then checks every 30 minutes
   */
  private scheduleUpdateChecks(): void {
    // Wait for app to stabilize before first check
    const appIsStable$ = this.appRef.isStable.pipe(
      first(isStable => isStable === true)
    );

    // Check every 30 minutes
    const every30Minutes$ = interval(30 * 60 * 1000);

    // Combine: check once stable, then every 30 minutes
    const checkSchedule$ = concat(appIsStable$, every30Minutes$);

    checkSchedule$.subscribe(async () => {
      await this.checkForUpdate();
    });
  }

  /**
   * Check for available updates
   */
  private async checkForUpdate(): Promise<void> {
    try {
      const updateFound = await this.updates.checkForUpdate();
      if (updateFound) {
        devLog('pwa-update', '🆕 Update available - will be prompted when ready');
      } else {
        devLog('pwa-update', '✅ Already on latest version');
      }
    } catch (err) {
      devError('pwa-update', '❌ Failed to check for updates:', err);
    }
  }

  /**
   * Listen for version updates
   */
  private listenForUpdates(): void {
    this.updates.versionUpdates
      .pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')
      )
      .subscribe(event => {
        devLog('pwa-update', '🆕 New version ready:', {
          current: event.currentVersion,
          latest: event.latestVersion
        });
        this.promptUserToUpdate();
      });
  }

  /**
   * Handle unrecoverable state
   * This happens when the service worker enters a broken state
   */
  private handleUnrecoverableState(): void {
    this.updates.unrecoverable.subscribe(event => {
      devError('pwa-update', '💥 Unrecoverable state:', event.reason);

      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('PWA.UPDATE.UNRECOVERABLE_TITLE'),
        detail: this.translate.instant('PWA.UPDATE.UNRECOVERABLE_MESSAGE'),
        sticky: true,
        closable: false
      });

      // Force reload after 3 seconds
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    });
  }

  /**
   * Prompt user about update with countdown
   */
  private promptUserToUpdate(): void {
    let secondsRemaining = this.UPDATE_COUNTDOWN_SECONDS;

    const updateMessage = () => {
      return this.translate.instant('PWA.UPDATE.AVAILABLE_MESSAGE', {
        seconds: secondsRemaining
      });
    };

    // Show initial notification
    this.messageService.add({
      key: 'pwa-update',
      severity: 'info',
      summary: this.translate.instant('PWA.UPDATE.AVAILABLE_TITLE'),
      detail: updateMessage(),
      sticky: true,
      closable: false
    });

    // Start countdown
    this.countdownInterval = setInterval(() => {
      secondsRemaining--;

      if (secondsRemaining > 0) {
        // Update notification with new countdown
        this.messageService.clear('pwa-update');
        this.messageService.add({
          key: 'pwa-update',
          severity: 'info',
          summary: this.translate.instant('PWA.UPDATE.AVAILABLE_TITLE'),
          detail: updateMessage(),
          sticky: true,
          closable: false
        });
      } else {
        // Countdown finished - apply update
        this.clearCountdown();
        this.applyUpdate();
      }
    }, 1000);
  }

  /**
   * Clear countdown interval
   */
  private clearCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = undefined;
    }
  }

  /**
   * Apply update with forced logout
   */
  private async applyUpdate(): Promise<void> {
    try {
      devLog('pwa-update', '🔄 Applying update...');

      try {
        localStorage.setItem(this.updateFlagKey, '1');
      } catch (storageError) {
        devWarn('pwa-update', '⚠️ Failed to persist update flag:', storageError);
      }

      // Show updating message
      this.messageService.clear('pwa-update');
      this.messageService.add({
        key: 'pwa-update',
        severity: 'info',
        summary: this.translate.instant('PWA.UPDATE.APPLYING_TITLE'),
        detail: this.translate.instant('PWA.UPDATE.APPLYING_MESSAGE'),
        sticky: true,
        closable: false
      });

      // Force logout if user is authenticated
      // This ensures all caches are cleared before update
      if (this.authStore.isLoggedIn()) {
        devLog('pwa-update', '🔒 Forcing logout to clear caches before update');
        this.authStore.clearAuth();
      }

      // Activate the new version
      await this.updates.activateUpdate();
      devLog('pwa-update', '✅ Update activated, reloading...');

      // Navigate to login (in case logout didn't navigate)
      await this.router.navigate(['/login']);

      // Reload the page to load new version
      // Small delay to ensure navigation completes
      setTimeout(() => {
        document.location.reload();
      }, 100);

    } catch (err) {
      devError('pwa-update', '❌ Failed to apply update:', err);

      try {
        localStorage.removeItem(this.updateFlagKey);
      } catch (storageError) {
        devWarn('pwa-update', '⚠️ Failed to clear update flag:', storageError);
      }

      this.messageService.clear('pwa-update');
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('PWA.UPDATE.FAILED_TITLE'),
        detail: this.translate.instant('PWA.UPDATE.FAILED_MESSAGE'),
        life: 5000
      });

      // Fallback: force reload anyway
      setTimeout(() => {
        document.location.reload();
      }, 2000);
    }
  }

  /**
   * Manually check for updates (can be called from UI)
   */
  async checkNow(): Promise<boolean> {
    if (!this.updates.isEnabled) {
      devWarn('pwa-update', 'Service Worker not enabled');
      return false;
    }

    try {
      const updateAvailable = await this.updates.checkForUpdate();
      if (!updateAvailable) {
        this.messageService.add({
          severity: 'info',
          summary: this.translate.instant('PWA.UPDATE.CHECK_TITLE'),
          detail: this.translate.instant('PWA.UPDATE.NO_UPDATE_AVAILABLE'),
          life: 3000
        });
      }
      return updateAvailable;
    } catch (err) {
      devError('pwa-update', 'Failed to check for updates:', err);
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('PWA.UPDATE.CHECK_TITLE'),
        detail: this.translate.instant('PWA.UPDATE.CHECK_FAILED'),
        life: 3000
      });
      return false;
    }
  }
}
